import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT } from "@/data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      className="fixed top-0 inset-x-0 z-40 bg-paper/85 backdrop-blur-xl border-b border-gray-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <img src="/assets/logo.png" alt="Fluton Global Metalcraft FZCO" className="h-10 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-tech text-xs uppercase tracking-[0.2em] transition-colors duration-200 hover:text-copper ${
                  isActive ? "text-copper" : "text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-quote-button"
            className="group inline-flex items-center gap-2 bg-navy text-white font-tech text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-copper transition-colors duration-300"
          >
            Get a Quote
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          className="md:hidden text-navy p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname + "-mobile"}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="md:hidden overflow-hidden bg-paper border-t border-gray-200"
            data-testid="nav-mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display font-bold text-2xl tracking-tight ${
                      isActive ? "text-copper" : "text-navy"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-quote-button"
                className="inline-flex items-center gap-2 bg-navy text-white font-tech text-xs uppercase tracking-[0.2em] px-5 py-3 w-fit"
              >
                Get a Quote <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
